import { Button, Space } from "antd-mobile";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import useSWR from "swr";
import clsx from "clsx";
import {
    fetchSpotlightTokens,
    fetchTrendingConfig,
    fetchTrendingList,
} from "~/api/endpoints";
import { IconSearch } from "~/components";
import InitLoading from "~/components/InitLoading";
import SimpleSegmentedControl from "../Market/SimpleSegmentedControl/SimpleSegmentedControl";
import NoResult from "~/components/NoResult";
import TokenList from "~/components/TokenList/TokenList";
import { useScrollPositionRestore } from "~/hook/useScrollPositionRestore";
import SpotlightToken from "./SpotlightToken";
import SpotlightTokenSkeleton from "./SpotlightTokenSkeleton";
import FireSticker from "~/components/LottieSticker/FireSticker";
import { formatPrice, formatVolume } from "~/utils/number-format";
import { Page } from "~/components/Page/Page";
import { sleep } from "~/tools/common";
import SafeAreaStickyTop from "~/components/widget/SafeAreaTop/SafeAreaStickyTop";
import Share2EarnBtn from "~/components/Share2Earn/Share2EarnBtn";

const TrendingPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { savePosition, restorePosition } = useScrollPositionRestore();

    const { data: spotlightData, isLoading: isSpotlightLoading } = useSWR(
        "fetchSpotlightTokens",
        fetchSpotlightTokens,
        {
            keepPreviousData: true,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        }
    );

    const rangeTypeCacheKey = `__uxuy_dex_cached_trending_range_type__`;
    const [rangeType, setRangType] = React.useState<string>();
    const { data: configData, isLoading: configLoading } = useSWR(
        "fetchTrendingConfig",
        fetchTrendingConfig,
        {
            keepPreviousData: true,
            revalidateIfStale: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
        }
    );

    useEffect(() => {
        const trendingRangeTypes = configData?.data?.types;
        if (trendingRangeTypes) {
            const selectType = configData?.data?.select_type;
            const cachecSelectType =
                window.localStorage.getItem(rangeTypeCacheKey);
            setRangType(
                cachecSelectType ??
                    selectType ??
                    String(trendingRangeTypes[0].range_type)
            );
        }
    }, [configData, rangeTypeCacheKey]);

    const queryTrendingList = (args: any) => {
        const [, rangeType] = args;
        if (!rangeType) return;
        return fetchTrendingList(rangeType);
    };

    const {
        data: trendingListData,
        isLoading: trendingLoading,
        mutate,
    } = useSWR(["fetchTrendingList", rangeType], queryTrendingList, {
        keepPreviousData: true,
    });

    useEffect(() => {
        restorePosition();
    }, [restorePosition, trendingListData]);

    const handleTokenClick = (token: any) => {
        savePosition();
        navigate(
            `/k-detail?chain_id=${token.token_base.chain_id}&contract=${token.token_base.contract}`
        );
    };

    if (configLoading) {
        return <InitLoading />;
    }

    const spotlightTokens = spotlightData?.data?.list ?? [];
    const showTopGainers =
        (isSpotlightLoading && spotlightTokens.length === 0) ||
        (!isSpotlightLoading && spotlightTokens.length > 0);
    const trendingRangeTypes = configData?.data.types;
    const trendingList = trendingListData?.data.list ?? [];
    const isEmptyList =
        !trendingLoading &&
        rangeType &&
        (!trendingList || trendingList.length === 0);

    const tokenColumns = [
        {
            key: "name",
            label: `${t("name")}`,
            value(token: any) {
                return `${t("volume")} ${formatVolume(token.token_market.volume_value)}`;
            },
            change() {
                return "";
            },
        },
        {
            key: "price",
            label: `${t("price")} / ${t("changes")}`,
            value(token: any) {
                return formatPrice(token.token_market.price);
            },
            change(token: any) {
                return token.token_market.change;
            },
        },
    ];

    return (
        <Page
            onRefresh={async () => {
                await mutate();
                await sleep(600);
            }}
        >
            <>
                {showTopGainers && (
                    <>
                        <div className="h-16 px-4 pt-[18px] pb-[10px] flex items-center justify-between">
                            <span className="text-h3 text-text_1 font-semibold">
                                {t("top_gainers")}
                            </span>
                            <div className="flex items-center">
                                <Button
                                    style={
                                        {
                                            height: 36,
                                            padding: 7,
                                            "--border-radius": "8px",
                                            "--border-color":
                                                "var(--color_border_2)",
                                            "--border-width": "0.5px",
                                            "--background-color": "transparent",
                                        } as React.CSSProperties
                                    }
                                    onClick={() => navigate(`/token-search`)}
                                >
                                    <IconSearch className="text-h5 text-text_2 " />
                                </Button>
                                <Share2EarnBtn className="ml-1.5" />
                            </div>
                        </div>
                        <Space className="w-full overflow-x-auto px-4 hidden-scrollbar">
                            {isSpotlightLoading
                                ? new Array(3)
                                      .fill(0)
                                      .map((_, index) => (
                                          <SpotlightTokenSkeleton key={index} />
                                      ))
                                : spotlightTokens.map((token, index) => (
                                      <SpotlightToken
                                          key={index}
                                          token={token}
                                          onClick={(token) => {
                                              handleTokenClick(
                                                  token.token_info
                                              );
                                          }}
                                      />
                                  ))}
                        </Space>
                    </>
                )}
                <SafeAreaStickyTop
                    id="homepage_trending_section"
                    className="bg-bg_0"
                    offsetTop="0.5px"
                >
                    <div
                        className={clsx(
                            "flex-1 px-4 pt-[18px] pb-[10px] flex items-center justify-between flex-wrap"
                        )}
                    >
                        <div
                            className="flex-none flex items-center"
                            style={{
                                width: "50%",
                            }}
                        >
                            <span
                                className={clsx(
                                    "text-h5 text-text_1 font-semibold"
                                )}
                            >
                                {t("trending")}
                            </span>
                            <FireSticker />
                        </div>
                        <div className={clsx("flex-none")}>
                            <SimpleSegmentedControl
                                className="w-[160px]"
                                options={trendingRangeTypes.map(
                                    (item: any) => ({
                                        label: item.range_name,
                                        value: String(item.range_type),
                                    })
                                )}
                                selectedValue={String(
                                    rangeType ||
                                        trendingRangeTypes[0].range_type
                                )}
                                onSelectedValueChange={(type) => {
                                    setRangType(String(type));
                                    window.localStorage.setItem(
                                        rangeTypeCacheKey,
                                        String(type)
                                    );
                                }}
                            />
                        </div>
                    </div>
                </SafeAreaStickyTop>
                {isEmptyList && <NoResult />}
                <TokenList
                    isLoading={trendingLoading || !rangeType}
                    tokens={trendingList}
                    columns={tokenColumns}
                    showHead={false}
                    layout="Page"
                    onTokenClick={handleTokenClick}
                />
            </>
        </Page>
    );
};

export default TrendingPage;
